<script>
import { Campaigns } from "@/graphql/queries";
import { isAuthError } from "@/lib/auth";

const PAGINATION_LIMIT = 5;

export default {
  apollo: {
    campaignsPaginated: {
      query: () => Campaigns,
      variables: {
        input: {
          limit: PAGINATION_LIMIT,
          paginationKey: "",
        },
      },
      manual: true, // disable the automatic property update for pagination to work. Need to specify a result callback below...
      result({ data, loading }) {
        if (!loading) {
          this.campaigns = data?.campaignsPaginated?.campaigns ?? [];
          this.paginationKey = data?.campaignsPaginated?.paginationKey ?? "";

          this.onCampaignsQueryResult();
        }
      },
      error({ graphQLErrors }) {
        this.reloadingCampaigns = false;

        if (graphQLErrors.length) {
          if (isAuthError(graphQLErrors)) {
            // If we have an auth error return false here as the login modal should open...
            return false;
          }

          this.errors = graphQLErrors.map(({ message }) => message);
        }
      },
    },
  },
  name: "CampaignsQueryLogic",
  data() {
    return {
      campaigns: [],
      paginationKey: "",
      reloadingCampaigns: false,
      status: "ALL",
    };
  },
  methods: {
    formatCampaign(campaigns) {
      return campaigns.map((campaign) => ({
        id: campaign?.campaignId ?? "",
        subject: campaign?.subject ?? "",
        dateCreated: campaign.dateCreated,
        dateSent: campaign?.stats?.dateSent ?? "",
        openCount: this.$has(campaign, "stats.openCount")
          ? campaign.stats.openCount || 0
          : "---",
        sentCount: this.$has(campaign, "stats.sentCount")
          ? campaign.stats.sentCount || 0
          : "---",
        clickCount: this.$has(campaign, "stats.clickCount")
          ? campaign.stats.clickCount || 0
          : "---",
        status: campaign?.status ?? "",
        previewUrl: campaign?.previewUrl ?? "",
      }));
    },
    onCampaignsQueryResult() {
      this.reloadingCampaigns = false;
    },
    onLoadMore() {
      const { paginationKey } = this;
      const variables = {
        input: {
          limit: PAGINATION_LIMIT,
          paginationKey: paginationKey,
        },
      };

      // Fetch more data and transform the original result
      this.$apollo.queries.campaignsPaginated.fetchMore({
        variables,
        // Transform the previous result with new data
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const previousItems =
            previousResult?.campaignsPaginated?.campaigns ?? [];
          const newItems = fetchMoreResult?.campaignsPaginated?.campaigns ?? [];
          const paginationKey =
            fetchMoreResult.campaignsPaginated.paginationKey ?? "";

          return {
            campaignsPaginated: {
              __typename: previousResult.campaignsPaginated.__typename,
              // Merging the items...
              campaigns: [...previousItems, ...newItems],
              // Update the paginationKey...
              paginationKey,
            },
          };
        },
      });
    },
    refetchCampaigns(status = "") {
      this.reloadingCampaigns = true;

      // When the status changes save it in state so that each refetch uses the same status
      // Only set status if it's passed in.
      if (status) this.status = status;

      const variables = {
        input: {
          limit: PAGINATION_LIMIT,
          paginationKey: "",
        },
      };

      // Add the status to the input if there is one otherwise return all campaigns...
      // If this.status is set to ALL we need to ignore it as ALL doesn't exist in the CampaignStatus enum in the schema.
      if (this.status && this.status !== "ALL") {
        variables.input.status = this.status;
      }

      this.$apollo.queries.campaignsPaginated.refetch(variables);
    },
  },
  render() {
    return this.$scopedSlots.default({
      campaigns: this.campaigns,
      campaignsErrors: this.errors,
      canLoadMore: this.paginationKey !== "",
      loadingCampaigns: this.$apollo.queries.campaignsPaginated.loading,
      loadMore: this.onLoadMore,
      reloadingCampaigns: this.reloadingCampaigns,
      refetchCampaigns: this.refetchCampaigns,
      status: this.status,
    });
  },
};
</script>
